import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Login from './pages/Login';
import Overview from './pages/Overview';
import Parts from './pages/Parts';
import InventoryLayout from './pages/InventoryLayout';
import InventoryMinMax from './pages/InventoryMinMax';
import InventoryHealth from './pages/InventoryHealth';
import InventoryGmroi from './pages/InventoryGmroi';
import InventoryVelocity from './pages/InventoryVelocity';
import InventoryTransfers from './pages/InventoryTransfers';
import InventoryCount from './pages/InventoryCount';
import InventoryCountNew from './pages/InventoryCountNew';
import InventoryCountSession from './pages/InventoryCountSession';
import Salespeople from './pages/Salespeople';
import Customer from './pages/Customer';
import Customers from './pages/Customers';
import Part from './pages/Part';
import AR from './pages/AR';
import LostSales from './pages/LostSales';
import Service from './pages/Service';
import Statement from './pages/Statement';
import Pagos from './pages/Pagos';
import Archives from './pages/Archives';
import Reports from './pages/Reports';
import Tablero from './pages/Tablero';
import AdminUsers from './pages/AdminUsers';
import AdminWriteAudit from './pages/AdminWriteAudit';
import TerminalUsers from './pages/TerminalUsers';
import Config from './pages/Config';
import PurchasingLayout from './pages/PurchasingLayout';
import PurchasingSuppliers from './pages/PurchasingSuppliers';
import PurchasingPurchases from './pages/PurchasingPurchases';
import PurchasingReceptions from './pages/PurchasingReceptions';
import PurchasingReturns from './pages/PurchasingReturns';
import SalesLayout from './pages/SalesLayout';
import SalesIndex from './pages/SalesIndex';
import SalesReturns from './pages/SalesReturns';
import SalesQuotes from './pages/SalesQuotes';
import Caja from './pages/Caja';
import Discrepancias from './pages/Discrepancias';
import Movil from './pages/Movil';
import Anticipos from './pages/Anticipos';
import PriceUpload from './pages/PriceUpload';
import PriceJobs from './pages/PriceJobs';
import PriceJobDetail from './components/PriceJobDetail';
import Reception from './pages/Reception';
import ReceptionNew from './pages/ReceptionNew';
import ReceptionScan from './pages/ReceptionScan';
import ReceptionReview from './pages/ReceptionReview';
import Pacesetter from './pages/Pacesetter';
import Inspecciones from './pages/dvi/Inspecciones';
import Capture from './pages/dvi/Capture';
import DviReport from './pages/public/DviReport';
import Comunicaciones from './pages/Comunicaciones';
import Payables from './pages/payables/Payables';
import PayableDetail from './pages/payables/PayableDetail';
import PayableVendors from './pages/payables/PayableVendors';
import SupportTickets from './pages/SupportTickets';
import SupportButton from './components/SupportButton';
import { currentUser } from './api/auth';
import type { User } from './api/types';
import GlobalProgress from './components/GlobalProgress';
import DrilldownProvider from './components/DrilldownProvider';
import DrilldownHost from './components/DrilldownHost';
import { LicenseBanner } from './components/LicenseBanner';


function PriceJobDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <PriceJobDetail jobId={id ?? ''} />
    </div>
  );
}

/**
 * Self-fetching sibling that surfaces the /api/me `user.license` state as a
 * global banner (Task 11). Same rationale as CountermanKioskRedirect below —
 * this app has no shared authed layout/user context, so a truly cross-route
 * banner has to live here, above <Routes>, self-fetching currentUser() and
 * re-fetching per navigation the same way (e.g. an owner renewing via
 * /admin/users and navigating elsewhere should see the banner clear without
 * a hard reload). Kept thin: all the actual bucket/tone/copy logic lives in
 * the prop-driven, directly-testable LicenseBanner component.
 */
function LicenseBannerContainer() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const location = useLocation();
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, [location.pathname]);
  return <LicenseBanner user={user} />;
}

/**
 * Kiosk gate for the `counterman` role — a shared terminal-only account
 * whose nav shows nothing but "Pacesetter" (every other topNav `show` gate
 * in Header.tsx evaluates false for it). Per the design spec, "the overview/
 * and any other route falls back to /pacesetter for this role" — a blanket
 * guard, not just a post-login landing redirect.
 *
 * This app has no shared "authed layout" route to hang that guard on: every
 * page self-fetches its own currentUser() independently (Reception.tsx,
 * Config.tsx, etc.), and even the owner→/movil mobile auto-route is
 * page-local (Overview.tsx's `isMobile && role === 'owner'` check), not
 * router-level. So instead this mirrors GlobalProgress/SupportButton — a
 * self-fetching sibling mounted above <Routes> that renders nothing — which
 * is the one place in this codebase a per-role redirect can act on every
 * route without threading the check through each page. It re-fetches on
 * every path change (not just once on mount) so a `counterman` freshly
 * logged in — which lands on "/" straight from Login.tsx's navigate('/') —
 * is caught immediately, not only after a later hard reload.
 */
function CountermanKioskRedirect() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, [location.pathname]);
  useEffect(() => {
    if (user?.role === 'counterman' && location.pathname !== '/pacesetter' && location.pathname !== '/login') {
      navigate('/pacesetter', { replace: true });
    }
  }, [user, location.pathname, navigate]);
  return null;
}

export default function App() {
  // Don't show the support button on /login (no session yet to attach a
  // ticket to) or on the public /inspeccion/:token DVI report (no session at
  // all — a customer's phone, not a staff device). Anywhere else, including
  // the staff-only /inspecciones entry point below, the button
  // feature-detects auth via /api/me on its own and self-hides if
  // unauthenticated. Deliberately NOT a `/inspeccion` prefix regex: that
  // would also swallow /inspecciones (staff page — needs the button) since
  // "inspeccion" is a string-prefix of "inspecciones".
  const path = useLocation().pathname;
  const bare = path === '/login' || path.startsWith('/inspeccion/');
  return (
    // DrilldownProvider sits inside the router (the modals render <Link>s) and
    // outside <Routes>, so an open drill-down survives a route change (JCD-59).
    <DrilldownProvider>
      <GlobalProgress />
      <LicenseBannerContainer />
      <CountermanKioskRedirect />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Overview />} />
        <Route path="/tablero" element={<Tablero />} />
        <Route path="/sales" element={<SalesLayout />}>
          <Route index element={<SalesIndex />} />
          <Route path="devoluciones" element={<SalesReturns />} />
          <Route path="cotizaciones" element={<SalesQuotes />} />
        </Route>
        <Route path="/parts" element={<Parts />} />
        <Route path="/inventory" element={<InventoryLayout />}>
          <Route index element={<InventoryHealth />} />
          <Route path="gmroi"     element={<InventoryGmroi />} />
          <Route path="velocidad" element={<InventoryVelocity />} />
          <Route path="traspasos" element={<InventoryTransfers />} />
          <Route path="conteo" element={<InventoryCount />} />
          <Route path="conteo/nuevo" element={<InventoryCountNew />} />
          <Route path="conteo/:id" element={<InventoryCountSession />} />
          <Route path="minmax" element={<InventoryMinMax />} />
        </Route>
        {/* /vendors lives under /purchasing/suppliers in v2.24. Old URLs redirect. */}
        <Route path="/vendors" element={<Navigate to="/purchasing/suppliers" replace />} />
        <Route path="/vendedores" element={<Salespeople />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:anum" element={<Customer />} />
        <Route path="/parts/:line/:part" element={<Part />} />
        <Route path="/ar" element={<AR />} />
        <Route path="/lost" element={<LostSales />} />
        <Route path="/service" element={<Service />} />
        <Route path="/estado" element={<Statement />} />
        {/* Aplicar pago — standalone payment-apply entry (Clientes menu); self-gates on canApplyPayments. */}
        <Route path="/pagos" element={<Pagos />} />
        <Route path="/archives" element={<Archives />} />
        <Route path="/reports" element={<Reports />} />
        {/* Customer ticket board (Linear JCD-20/JCD-21) — a site's own JCD
            tickets as a status board; follows the same flat-route, self-gating
            posture as the other standalone pages here. */}
        <Route path="/soporte/tickets" element={<SupportTickets />} />
        <Route path="/caja" element={<Caja />} />
        <Route path="/discrepancias" element={<Discrepancias />} />
        <Route path="/pacesetter" element={<Pacesetter />} />
        <Route path="/movil" element={<Movil />} />
        <Route path="/anticipos" element={<Anticipos />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/write-audit" element={<AdminWriteAudit />} />
        <Route path="/admin/terminal-users" element={<TerminalUsers/>} />
        <Route path="/admin/config" element={<Config />} />
        <Route path="/purchasing" element={<PurchasingLayout />}>
          <Route index element={<Navigate to="suppliers" replace />} />
          <Route path="suppliers"  element={<PurchasingSuppliers />} />
          <Route path="purchases"  element={<PurchasingPurchases />} />
          <Route path="receptions" element={<PurchasingReceptions />} />
          <Route path="returns"    element={<PurchasingReturns />} />
        </Route>
        {/* Order Reception ("Recepción") — mobile-first scanner-driven PO
            receiving, reached from both Compras and Móvil (see PurchasingLayout
            / Movil). One route family regardless of entry point. */}
        <Route path="/po/scan" element={<Reception />} />
        <Route path="/po/scan/nuevo" element={<ReceptionNew />} />
        <Route path="/po/scan/:id" element={<ReceptionScan />} />
        <Route path="/po/scan/:id/revision" element={<ReceptionReview />} />
        {/* DVI (Digital Vehicle Inspection) — /inspecciones is the standalone
            staff-reachable entry point (recent list + "start by WO#"); the
            Service cockpit's Dvi tab (owner/manager only) is a second door
            into the same capture/report routes below. */}
        <Route path="/inspecciones" element={<Inspecciones />} />
        <Route path="/dvi/captura/:sid/:tnum" element={<Capture />} />
        <Route path="/inspeccion/:token" element={<DviReport />} />
        {/* Service Comms — staff outbox view of customer reminders (DVI-ready,
            appointment, service-due, approval-followup). Standalone
            self-gated page (Comunicaciones.tsx checks canComms itself), same
            posture as /inspecciones above. */}
        <Route path="/comunicaciones" element={<Comunicaciones />} />
        {/* Cuentas por Pagar — paceBI-owned supplier ledger, zero ERP write.
            Each page self-gates on features.payables_enabled + ap:view.
            /pagar/proveedores is declared BEFORE /pagar/:id so the literal
            path is not swallowed by the parameter. */}
        <Route path="/pagar" element={<Payables />} />
        <Route path="/pagar/proveedores" element={<PayableVendors />} />
        <Route path="/pagar/:id" element={<PayableDetail />} />
        <Route path="/price-uploads" element={<PriceJobs />} />
        <Route path="/price-uploads/new" element={<PriceUpload />} />
        <Route path="/price-uploads/:id" element={<PriceJobDetailRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <DrilldownHost />
      {!bare && <SupportButton />}
    </DrilldownProvider>
  );
}
